import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Alert,
  CircularProgress,
  keyframes,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Search as SearchIcon,
  PlayArrow as PlayIcon,
  Edit as EditIcon,
  ContentCopy as ContentCopyIcon,
  DeleteOutline as DeleteIcon,
  MoreVert as MoreVertIcon,
  BugReport as BugReportIcon,
} from '@mui/icons-material';
import { Scenario } from '../../types/attacks';
import ScenarioRowContent, { ATTACK_TYPE_CHIP_PADDING_CH } from './ScenarioRowContent';
import { useTimezone } from '../../contexts/TimezoneContext';
import { formatUiDateOnly, formatUiTimeWithSeconds } from '../../utils/dateUtils';

interface ScenarioSelectionViewProps {
  scenarios: Scenario[];
  loading: boolean;
  error: string | null;
  onScenarioSelect: (scenario: Scenario) => void;
  onScenarioRunDebug?: (scenario: Scenario) => void;
  onScenarioEdit?: (scenario: Scenario) => void;
  onScenarioDuplicate?: (scenario: Scenario) => void;
  onScenarioDelete?: (scenario: Scenario) => void;
  onCreateNew: () => void;
  settingScenario?: boolean;
  activeScenarioId?: string | null;
  /** scenarioId -> true if all targets in allow list; false if not; undefined while loading */
  scenarioAllowListOk?: Record<string, boolean>;
  /** scenarioId -> list of target strings not in the allow list */
  scenarioInvalidTargets?: Record<string, string[]>;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ScenarioSelectionView: React.FC<ScenarioSelectionViewProps> = ({
  scenarios,
  loading,
  error,
  onScenarioSelect,
  onScenarioRunDebug,
  onScenarioEdit,
  onScenarioDuplicate,
  onScenarioDelete,
  onCreateNew,
  settingScenario = false,
  activeScenarioId,
  scenarioAllowListOk = {},
  scenarioInvalidTargets = {},
}) => {
  const theme = useTheme();
  const { timezone } = useTimezone();
  const isDark = theme.palette.mode === 'dark';
  const [searchTerm, setSearchTerm] = useState('');
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionsMenuScenario, setActionsMenuScenario] = useState<Scenario | null>(null);

  const filteredScenarios = useMemo(() => {
    if (!searchTerm.trim()) return scenarios;
    const lowerSearch = searchTerm.toLowerCase();
    return scenarios.filter(scenario =>
      scenario.name.toLowerCase().includes(lowerSearch) ||
      scenario.description?.toLowerCase().includes(lowerSearch) ||
      scenario.attack_type?.name.toLowerCase().includes(lowerSearch)
    );
  }, [scenarios, searchTerm]);

  const attackTypeChipMinWidthCh = useMemo(() => {
    if (filteredScenarios.length === 0) return 12;
    const maxLen = Math.max(
      ...filteredScenarios.map((s) => (s.attack_type?.name?.length ?? 0)),
      12
    );
    return Math.min(maxLen, 40);
  }, [filteredScenarios]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          gap: 3,
        }}
      >
        <CircularProgress
          size={60}
          sx={{
            color: theme.palette.primary.main,
            filter: `drop-shadow(0 0 20px ${alpha(theme.palette.primary.main, 0.6)})`,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontFamily: '"Space Mono", monospace',
            color: 'text.secondary',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}
        >
          Loading Scenarios...
        </Typography>
        <Box
          sx={{
            width: '200px',
            height: '3px',
            background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
            animation: `${fadeIn} 1.5s ease-in-out infinite`,
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        animation: `${fadeIn} 0.6s ease-out`,
        position: 'relative',
      }}
    >
      {/* Loading Overlay for Setting Scenario */}
      {settingScenario && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            gap: 3,
          }}
        >
          <CircularProgress
            size={80}
            sx={{
              color: theme.palette.primary.main,
              filter: `drop-shadow(0 0 30px ${alpha(theme.palette.primary.main, 0.8)})`,
            }}
          />
          <Typography
            variant="h5"
            sx={{
              fontFamily: '"Space Mono", monospace',
              color: theme.palette.primary.main,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              textShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.6)}`,
            }}
          >
            Activating Scenario...
          </Typography>
          <Box
            sx={{
              width: '300px',
              height: '3px',
              background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
              animation: `${fadeIn} 1.5s ease-in-out infinite`,
            }}
          />
        </Box>
      )}

      {/* Search and Create Bar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          fullWidth
          placeholder="Search scenarios by name, type, or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.primary.main }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flexGrow: 1,
            '& .MuiOutlinedInput-root': { fontFamily: '"Space Mono", monospace' },
          }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreateNew}
          sx={{
            fontFamily: '"Space Mono", monospace',
            fontWeight: 600,
            textTransform: 'uppercase',
            px: 4,
            whiteSpace: 'nowrap',
          }}
        >
          Create New
        </Button>
      </Box>

      {/* Error Display */}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {activeScenarioId && (
        <Alert severity="info" sx={{ mb: 3, fontFamily: '"Space Mono", monospace', fontSize: '0.85rem' }}>
          A scenario is currently active. End it before activating a new one.
        </Alert>
      )}

      {/* Scenarios Table — two-line rows, no horizontal scroll */}
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'hidden', maxWidth: '100%' }}>
          <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 2, pl: 4, pr: 3, fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem', fontFamily: '"Space Mono", monospace' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Box sx={{ minWidth: `${attackTypeChipMinWidthCh + ATTACK_TYPE_CHIP_PADDING_CH}ch`, flexShrink: 0 }}>Attack Type</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>Target/s</Box>
                    <Box sx={{ flexShrink: 0, marginLeft: 'auto' }}>Last Execution</Box>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ py: 2, pl: 3, pr: 5, fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem', width: 96, minWidth: 96 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
          <TableBody>
            {filteredScenarios.length === 0 && (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 4, textAlign: 'center', fontFamily: '"Space Mono", monospace' }}
                  >
                    {searchTerm
                      ? 'No scenarios match your search'
                      : 'No scenarios yet. Create one to get started.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filteredScenarios.map((scenario) => {
              const isActive = scenario.id === activeScenarioId;
              const hasActiveOther = Boolean(activeScenarioId) && !isActive;
              const targets = Array.isArray((scenario.configuration as any)?.targets)
                ? (scenario.configuration as any).targets as string[]
                : [];
              return (
                <TableRow
                  key={scenario.id}
                  sx={{
                    opacity: hasActiveOther ? 0.5 : 1,
                    transition: 'opacity 0.2s',
                    '&:hover': { backgroundColor: alpha(theme.palette.action.hover, isDark ? 0.06 : 0.04) },
                  }}
                >
                  <TableCell sx={{ py: 2, px: 3, pr: 4, overflow: 'hidden', minWidth: 0 }}>
                    <ScenarioRowContent
                      scenarioName={scenario.name}
                      isActive={isActive}
                      targetsNotInAllowList={scenarioInvalidTargets[scenario.id] ?? []}
                      attackTypeName={scenario.attack_type?.name ?? ''}
                      attackClass={scenario.attack_type?.attack_class ?? ''}
                      attackTypeDescription={scenario.attack_type?.description}
                      dateLabel={scenario.updated_at ? formatUiDateOnly(scenario.updated_at, timezone) : '—'}
                      timeLabel={scenario.updated_at ? formatUiTimeWithSeconds(scenario.updated_at, timezone) : undefined}
                      targets={targets}
                      rowId={scenario.id}
                      attackTypeChipMinWidthCh={attackTypeChipMinWidthCh}
                    />
                  </TableCell>

                  <TableCell align="right" sx={{ py: 2, pl: 3, pr: 5, width: 96, minWidth: 96, verticalAlign: 'middle' }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActionsMenuAnchor(e.currentTarget);
                        setActionsMenuScenario(scenario);
                      }}
                      aria-label="Scenario actions"
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      <Menu
        anchorEl={actionsMenuAnchor}
        open={Boolean(actionsMenuAnchor)}
        onClose={() => {
          setActionsMenuAnchor(null);
          setActionsMenuScenario(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {actionsMenuScenario && (
          <>
            <MenuItem
              disabled={Boolean(activeScenarioId) || scenarioAllowListOk[actionsMenuScenario.id] === false}
              onClick={() => {
                if (actionsMenuScenario) onScenarioSelect(actionsMenuScenario);
                setActionsMenuAnchor(null);
                setActionsMenuScenario(null);
              }}
            >
              <ListItemIcon><PlayIcon fontSize="small" /></ListItemIcon>
              <ListItemText>
                {actionsMenuScenario.id === activeScenarioId ? 'Active' : 'Activate'}
              </ListItemText>
            </MenuItem>
            {onScenarioRunDebug && (
              <MenuItem
                disabled={
                  Boolean(activeScenarioId) ||
                  scenarioAllowListOk[actionsMenuScenario.id] === false
                }
                onClick={() => {
                  if (actionsMenuScenario) onScenarioRunDebug(actionsMenuScenario);
                  setActionsMenuAnchor(null);
                  setActionsMenuScenario(null);
                }}
              >
                <ListItemIcon><BugReportIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Run Debug</ListItemText>
              </MenuItem>
            )}
            {onScenarioEdit && (
              <MenuItem
                onClick={() => {
                  if (actionsMenuScenario) onScenarioEdit(actionsMenuScenario);
                  setActionsMenuAnchor(null);
                  setActionsMenuScenario(null);
                }}
              >
                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Edit</ListItemText>
              </MenuItem>
            )}
            {onScenarioDuplicate && (
              <MenuItem
                onClick={() => {
                  if (actionsMenuScenario) onScenarioDuplicate(actionsMenuScenario);
                  setActionsMenuAnchor(null);
                  setActionsMenuScenario(null);
                }}
              >
                <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Duplicate</ListItemText>
              </MenuItem>
            )}
            {onScenarioDelete && (
              <>
                <Divider />
                <MenuItem
                  disabled={actionsMenuScenario.id === activeScenarioId}
                  onClick={() => {
                    if (actionsMenuScenario) onScenarioDelete(actionsMenuScenario);
                    setActionsMenuAnchor(null);
                    setActionsMenuScenario(null);
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </MenuItem>
              </>
            )}
          </>
        )}
      </Menu>

      {filteredScenarios.length > 0 && (
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', fontFamily: '"Space Mono", monospace', mt: 1.5 }}
        >
          {filteredScenarios.length} scenario{filteredScenarios.length !== 1 ? 's' : ''} found
        </Typography>
      )}
    </Box>
  );
};

export default ScenarioSelectionView;
